import { useEffect, useState, type JSX } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import { Checkbox } from '@/components/ui/checkbox.js';

import { useSkillCopyTargets, type CopyTarget } from './useSkillCopyTargets.js';

/**
 * Picks the workspaces a workspace skill is copied into. Each pick gets an independent copy; the
 * list holds only the other shared workspaces the caller can author in.
 */
export function CopyToWorkspacesDialog({
  open,
  onOpenChange,
  trigger,
  sourceWorkspaceId,
  onCopy,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: string;
  sourceWorkspaceId: string | null;
  onCopy: (targets: CopyTarget[]) => void;
}): JSX.Element {
  const { targets, loading, failed } = useSkillCopyTargets(
    sourceWorkspaceId,
    open,
  );
  const [pickedIds, setPickedIds] = useState<string[]>([]);
  /** Each opening starts with nothing ticked, so a cancelled pick is never carried over. */
  useEffect(() => {
    if (open) setPickedIds([]);
  }, [open]);

  /** Ticks or clears one workspace. */
  function toggle(id: string, checked: boolean): void {
    setPickedIds((current) =>
      checked ? [...current, id] : current.filter((picked) => picked !== id),
    );
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Copy /${trigger} to other workspaces`}
      description="Each workspace gets its own copy. Changes to one do not reach the others."
    >
      <div className="mt-3 flex flex-col gap-4">
        {failed && (
          <p className="text-sm text-destructive">
            Some of your workspaces could not be checked. Close this and try
            again.
          </p>
        )}
        {targets.length === 0 && !loading && !failed ? (
          <p className="text-sm text-muted-foreground">
            You are not an owner or editor of any other shared workspace.
          </p>
        ) : (
          <div className="flex max-h-64 flex-col gap-2 overflow-y-auto">
            {targets.map((target) => (
              <label
                key={target.id}
                className="flex min-w-0 items-center gap-2 text-sm"
              >
                <Checkbox
                  checked={pickedIds.includes(target.id)}
                  onCheckedChange={(checked) =>
                    toggle(target.id, checked === true)
                  }
                />
                <span className="truncate">{target.name}</span>
              </label>
            ))}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {targets.length > 0 && (
            <Button
              size="sm"
              disabled={pickedIds.length === 0}
              onClick={() => {
                onOpenChange(false);
                onCopy(
                  targets.filter((target) => pickedIds.includes(target.id)),
                );
              }}
            >
              Copy
            </Button>
          )}
        </div>
      </div>
    </AppDialog>
  );
}
